/**
 * The wheel, as geometry. Two screens draw a roulette ring — the TV's live
 * wheel and the landing page's demo — and both have to solve the same awkward
 * thing: the ring is turning under the ball, so where the ball *lands* is not
 * where the ball's own angle points. That solve lives here once, with the arc
 * maths it needs, rather than being copied and drifting.
 *
 * Every function works in the wheel's own 200x200 view box, centre (100, 100),
 * with 0 degrees straight up and angles running clockwise — the same frame the
 * shared `wheelOrder` indexes into, so a pocket's index is its angular home.
 */

/** A point on a circle around (cx, cy), `deg` clockwise from straight up. */
export const polar = (cx: number, cy: number, r: number, deg: number): [number, number] => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

/**
 * One fret of the ring: the wedge of the annulus between radii `r0` and `r1`
 * that pocket `i` of `n` owns. Each side is pulled in by a third of a degree so
 * neighbouring frets read as separate pockets rather than one poured ring.
 */
export const segmentPath = (i: number, n: number, r0: number, r1: number): string => {
  const seg = 360 / n;
  const a0 = i * seg + 0.35;
  const a1 = (i + 1) * seg - 0.35;
  const [x0, y0] = polar(100, 100, r1, a0);
  const [x1, y1] = polar(100, 100, r1, a1);
  const [x2, y2] = polar(100, 100, r0, a1);
  const [x3, y3] = polar(100, 100, r0, a0);
  return `M ${x0} ${y0} A ${r1} ${r1} 0 0 1 ${x1} ${y1} L ${x2} ${y2} A ${r0} ${r0} 0 0 0 ${x3} ${y3} Z`;
};

/**
 * How fast the ring turns against the ball, as a share of the ball's own
 * angle. The ball's bearing over the ring is therefore `(1 + RING_RATIO)`
 * times its angle, which is what the landing maths has to solve for.
 */
export const RING_RATIO = 0.35;

/**
 * The ball's new cumulative angle so that it comes to rest over pocket `index`.
 *
 * `cumulative` is how far the ball has already travelled across the night, in
 * degrees; the answer is always larger, so a wheel can be driven by handing
 * this straight to one CSS transition per spin and never unwinding. The ball
 * takes `laps` full turns over the ring before it settles, and it aims at the
 * middle of the fret rather than its leading edge — a ball parked on a fret
 * line reads as a wheel that has not decided.
 */
export function ballAngle(
  cumulative: number,
  index: number,
  pockets: number,
  laps = 4,
  ringRatio = RING_RATIO,
): number {
  const seg = 360 / pockets;
  const targetBearing = index * seg + seg / 2;
  // Where the ball sits over the ring right now, and how much further round
  // the ring it has to go — solved over the relative bearing, then divided
  // back down into an angle for the ball itself.
  const bearing = cumulative * (1 + ringRatio);
  const targetMod = (((targetBearing - bearing) % 360) + 360) % 360;
  return (bearing + 360 * laps + targetMod) / (1 + ringRatio);
}

/** Where the ball sits over the ring for a given ball angle: its fret bearing. */
export const ballBearing = (angle: number, ringRatio = RING_RATIO): number =>
  angle * (1 + ringRatio);
